/**
 * Instrument approaches: the published ones from `x_atc.approaches`, or a synthesized straight-in
 * approach for runway ends without data, and their resolved points for charts and the scope.
 *
 * Synthesis (an assumption, see docs/airport-format.md): the final approach point is where the
 * glideslope passes threshold elevation + 2000 ft rounded up to 500 ft; the intermediate fix is
 * 4 nm before it at the same altitude; the missed approach is runway heading, which matches the
 * go-around the sim flies.
 */
import { findRunwayEnd, type AirportData, type InstrumentApproach, type LatLon, type Restriction, type RunwayEnd } from './airport';
import { alongTrackM, destination, NM_TO_M } from './geo/projection';

const FT_PER_NM = 6076.12;
/** Distance of the intermediate fix before the final approach point, nm. */
export const SYNTH_IF_BEFORE_FAP_NM = 4;
/** Length of the drawn missed-approach stub, nm. */
export const MISSED_STUB_NM = 5;

/** Distance from the threshold at which a glideslope reaches `heightFt` above it, nm. */
export function glideslopeDistanceNm(heightFt: number, glideslopeDeg: number): number {
  return heightFt / (Math.tan((glideslopeDeg * Math.PI) / 180) * FT_PER_NM);
}

export function synthesizeApproach(end: RunwayEnd): InstrumentApproach {
  const thrFt = end.elevationM / 0.3048;
  const fapFt = Math.ceil((thrFt + 2000) / 500) * 500;
  const type = end.ils ? 'ils' : 'visual';
  return {
    id: `${type.toUpperCase()}${end.name}`,
    name: `${end.ils ? 'ILS' : 'Visual'} RWY ${end.name}`,
    type,
    runway: end.name,
    legs: [
      { fix: `IF${end.name}`, role: 'IF', restriction: { altitudeFt: fapFt } },
      { fix: `FAP${end.name}`, role: 'FAF', restriction: { altitudeFt: fapFt } },
    ],
    source: 'synthesized',
  };
}

const TYPE_ORDER = ['ils', 'loc', 'rnp', 'visual'] as const;

/** The approach a controller would expect for a runway: published ILS, LOC, RNP, else synthesized. */
export function defaultApproach(airport: AirportData, runway: string): InstrumentApproach | undefined {
  const published = airport.approaches.filter((a) => a.runway === runway).sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type));
  if (published[0]) return published[0];
  const end = findRunwayEnd(airport, runway);
  return end ? synthesizeApproach(end) : undefined;
}

/** Short radio/scope label: "ILS 12", "RNP 30", "VIS 08". */
export function approachLabel(a: InstrumentApproach): string {
  return `${a.type === 'visual' ? 'VIS' : a.type.toUpperCase()} ${a.runway}`;
}

export interface ApproachPoint extends LatLon { name: string; role?: string; restriction?: Restriction }
export interface ApproachPath {
  points: ApproachPoint[];
  threshold: RunwayEnd;
  missed: LatLon[];
}

/** Resolved points from the first approach fix to the threshold, plus the missed approach. */
export function approachPath(airport: AirportData, a: InstrumentApproach): ApproachPath | undefined {
  const thr = findRunwayEnd(airport, a.runway);
  if (!thr) return undefined;
  const back = (thr.headingDeg + 180) % 360;
  const thrFt = thr.elevationM / 0.3048;
  const points: ApproachPoint[] = [];
  for (const leg of a.legs) {
    let at: LatLon | undefined;
    if (a.source === 'synthesized') {
      const alt = leg.restriction?.altitudeFt ?? thrFt + 2000;
      const fapNm = glideslopeDistanceNm(alt - thrFt, thr.glideslopeDeg || 3);
      const nm = leg.role === 'IF' ? fapNm + SYNTH_IF_BEFORE_FAP_NM : fapNm;
      at = destination(thr.lat, thr.lon, back, nm * NM_TO_M);
    } else {
      at = airport.fixes.get(leg.fix);
    }
    if (at) points.push({ name: leg.fix, lat: at.lat, lon: at.lon, ...(leg.role ? { role: leg.role } : {}), ...(leg.restriction ? { restriction: leg.restriction } : {}) });
  }
  const missed: LatLon[] = [{ lat: thr.lat, lon: thr.lon }];
  let cur: LatLon = missed[0]!;
  for (const leg of a.missed ?? [{ kind: 'heading' as const, headingDeg: thr.headingDeg }]) {
    if (leg.kind === 'fix') { const f = airport.fixes.get(leg.fix); if (f) { missed.push(f); cur = f; } }
    else { cur = destination(cur.lat, cur.lon, leg.headingDeg, MISSED_STUB_NM * NM_TO_M); missed.push(cur); }
  }
  return { points, threshold: thr, missed };
}

/**
 * Where radar vectors may join a runway's final. PANS-ATM 8.9.4: the vector puts the aircraft on
 * the final approach track before the glide path, level, at an intercept angle of 30° (45° at
 * most). Assumption: "before the glide path" is the published intermediate fix (ILS 12 at LLBG:
 * BG94D, 9.4 nm, 2 500 ft), and the joining level is its altitude rounded up to a thousand feet,
 * never so low that the glideslope passes overhead before the join.
 */
export interface FinalJoin {
  threshold: RunwayEnd;
  /** The closest a vector may join the localizer, nm from the threshold. */
  minNm: number;
  /** Level to be at on the join. */
  altitudeFt: number;
}

export function finalJoin(airport: AirportData, runway: string): FinalJoin | undefined {
  const approach = defaultApproach(airport, runway);
  const path = approach && approachPath(airport, approach);
  if (!path) return undefined;
  const thr = path.threshold;
  const thrFt = thr.elevationM / 0.3048;
  const back = (p: LatLon) => -alongTrackM(thr.lat, thr.lon, thr.headingDeg, p.lat, p.lon) / NM_TO_M;
  const intermediate = path.points.find((p) => p.role === 'IF');
  const final = path.points.find((p) => p.role === 'FAF');
  const ifFt = intermediate?.restriction?.altitudeFt ?? intermediate?.restriction?.altitudeMinFt ?? final?.restriction?.altitudeFt ?? thrFt + 2000;
  const altitudeFt = Math.ceil(ifFt / 1000) * 1000;
  const ifNm = intermediate ? back(intermediate) : final ? back(final) + 2 : glideslopeDistanceNm(2000, thr.glideslopeDeg || 3) + SYNTH_IF_BEFORE_FAP_NM;
  return { threshold: thr, minNm: Math.max(ifNm, glideslopeDistanceNm(altitudeFt - thrFt, thr.glideslopeDeg || 3)), altitudeFt };
}
